import isError from "lodash/isError";
import isString from "lodash/isString";
import qs, {
	type ParseOptions,
	type ParsedUrl,
	type StringifiableRecord,
	type StringifyOptions,
	type UrlObject,
} from "query-string";

import { type ApiError, type Dto, isApiError, isDto, isErrorsResponse } from "~/models/dto";
import { hasProperty } from "~/tools/has-property";
import { isArray } from "~/tools/is-array";
import { isPresent } from "~/tools/is-present";
import type { Record } from "~/tools/record";
import {
	type RequestStatus,
	type ResponseStatus,
	errorStatus,
	loadingStatus,
	successStatus,
} from "~/tools/request-status";
import { type Observable, from, startWith } from "~/tools/rxjs";

import type { Api } from "../common/openapi";
import { isMaintenanceBoundaryActive } from "../components/maintenance-boundary/state";

import { fetch } from "./fetch";

/**
 * Generic wrapper for fetch with handled response
 * @param path - URL of the resource you want to fetch
 * @param params - custom settings for request
 *    by default:
 *        header: Content-type is application/json
 *        method: GET
 *        credentials: same-origin
 */

interface HTTPErrorParams {
	statusText: string;
	message?: string;
	apiErrors?: Array<ApiError>;
	dto?: Dto;
	statusCode?: number;
	url?: string;
	data?: unknown;
}

export class HTTPError extends Error implements HTTPErrorParams {
	public apiErrors?: Array<ApiError>;
	public statusText: string;
	public dto?: Dto;
	public statusCode?: number;
	public url?: string;
	public data?: unknown;
	public name = "HTTPError";

	constructor({ statusText, message, apiErrors, dto, statusCode, url, data }: HTTPErrorParams) {
		super(message); // 'Error' breaks prototype chain here
		this.statusText = statusText;
		this.apiErrors = apiErrors;
		this.dto = dto;
		this.statusCode = statusCode;
		this.url = url;
		this.data = data;
	}

	public shouldRetry(): boolean {
		return (
			this.statusCode != null &&
			// too many requests https://http.cat/429 OR any server error
			(this.statusCode === 429 || this.statusCode >= 500)
		);
	}
}

// Must be in sync with CSRF header from backend!
const CSRF_HEADER_NAME = "X-Sts-Token";

export const CSRF_TOKEN_COOKIE = "pac4jCsrfToken";

export const isHTTPError = (error: any): error is HTTPError => error instanceof HTTPError;
export const isStackElementNotFoundResponse = (error: any): error is Api.StackElementNotFound =>
	error && error._type === "StackElementNotFound";

export class NotFoundError extends HTTPError {
	public readonly name = "NotFoundError";
	public readonly statusCode = 404;
}

export const isTypologyOverflowError = (error: any): error is TypologyOverflowError =>
	error instanceof TypologyOverflowError;

export class TypologyOverflowError extends HTTPError {
	public readonly name = "TypologyOverflowError";
	public readonly statusCode = 400;
}

export class ForbiddenError extends HTTPError {
	public readonly name = "ForbiddenError";
	public readonly statusCode = 403;
}

export const isUnauthorizedError = (error: any): error is UnauthorizedError => error instanceof UnauthorizedError;

export class UnauthorizedError extends HTTPError {
	public readonly name = "UnauthorizedError";
	public readonly statusCode = 401;
}

const stringifyJsonOnes = (value: unknown): string => {
	if (typeof value !== "string") {
		return JSON.stringify(value);
	}

	try {
		// if the string is a JSON, the parse should not fail
		return JSON.stringify(JSON.parse(value));
	} catch {
		// fails when the value is a string but not a JSON so it should be stringified
		return JSON.stringify(value);
	}
};

const isCors = (path: string): boolean => {
	try {
		const url = new URL(path);

		return url.origin !== location.origin;
	} catch {
		return false;
	}
};

export type RequestAcceptParam = "json" | "markdown" | "text" | "*";

const createAcceptHeaders = (accept: RequestAcceptParam, cors: boolean): Record<string, string> => {
	if (accept === "*") {
		return {
			Accept: "*/*",
			"Content-Type": "application/json; charset=utf-8",
		};
	}

	if (accept === "json") {
		return {
			Accept: "application/json",
			"Content-Type": "application/json; charset=utf-8",
		};
	}

	if (accept === "text") {
		return {
			Accept: "text/plain",
			"Content-Type": "text/plain; charset=utf-8",
		};
	}

	// markdown
	if (!cors) {
		return {
			Accept: "text/markdown",
			"Content-Type": "text/markdown; charset=utf-8",
		};
	}

	return {
		Accept: "text/plain",
		"Content-Type": "text/plain",
	};
};

const createKeepAliveHeaders = (keepAlive: undefined | RequestKeepAliveParam): undefined | Record<string, string> => {
	if (keepAlive == null) {
		return undefined;
	}

	return {
		Connection: "Keep-Alive",
		"Keep-Alive": `timeout=${keepAlive.timeoutSeconds}, max=${keepAlive.max}`,
	};
};

const createCsrfHeaders = (): Record<string, string> => {
	const csrfToken = document.cookie
		.split(";")
		.find((c) => c.trim().startsWith(`${CSRF_TOKEN_COOKIE}=`))
		?.split("=")[1]
		.trim();

	if (csrfToken) {
		return {
			[CSRF_HEADER_NAME]: csrfToken,
		};
	}
	return {};
};

export interface RequestKeepAliveParam {
	timeoutSeconds: number;
	max: number;
}

export interface RequestParams {
	/**
	 * HTTP method of the request.
	 *
	 * @default "GET"
	 */
	method?: "GET" | "POST" | "PUT" | "DELETE";

	/**
	 * Keep alive headers of the request.
	 */
	keepAlive?: RequestKeepAliveParam;

	/**
	 * Accept headers of the request.
	 *
	 * @default "json"
	 */
	accept?: RequestAcceptParam;

	/**
	 * Query parameters of the request. They will be merged with the query parameters in the path.
	 *
	 * @example
	 *
	 * request("/api/snapshot", {
	 *  query: {
	 *   start: 0,
	 *   end: 1000,
	 *  }
	 * }) // will send request to /api/snapshot?start=0&end=1000
	 *
	 * request("/api/snapshot?start=0&step=100", {
	 *  query: {
	 *   start: 500,
	 *   end: 1000,
	 *  }
	 * }) // will send request to /api/snapshot?start=500&step=100&end=1000
	 */
	query?: StringifiableRecord;
	queryOptions?: StringifyOptions;
	body?: unknown;
	headers?: Record<string, string>;
}

export const request = <T = unknown>(
	path: string,
	{ method, query, queryOptions, keepAlive, accept = "json", body, headers }: RequestParams = {},
): Promise<T> => {
	const { url: cleanPath, query: queryInPath } = parseUrl(path);
	const fullUrl = stringifyUrl(
		{
			url: cleanPath,
			query: { ...queryInPath, ...query },
		},
		queryOptions,
	);
	const stringifiedBody = body !== undefined && accept === "json" ? stringifyJsonOnes(body) : (body as BodyInit);

	const req = isCors(cleanPath)
		? // biome-ignore lint/style/noRestrictedGlobals: encapsulation
			window.fetch(fullUrl, {
				mode: "cors",
				method,
				headers: {
					...createAcceptHeaders(accept, true),
					...createKeepAliveHeaders(keepAlive),
				},
				body: stringifiedBody,
			})
		: fetch(fullUrl, {
				credentials: "same-origin",
				method,
				headers: {
					...createAcceptHeaders(accept, false),
					...createKeepAliveHeaders(keepAlive),
					...createCsrfHeaders(),
					...headers,
				},
				body: stringifiedBody,
			});

	return req
		.then(isOkResponse)
		.then(parseResponse as (response: Response) => Promise<T>)
		.catch(handleCatch);
};

/**
 * Check if response is OK. (status code between 200 and 400)
 * if it's not ok - throw response to handle in catch
 */
const isOkResponse = (res: Response): Response => {
	if (res.ok) {
		return res;
	}

	throw res;
};

/**
 * Parse response based on header
 * @returns json|text based on header
 */
const parseResponse = (response: Response): Promise<unknown> => {
	if (response.body == null) {
		return Promise.resolve(undefined);
	}

	const isJSON = response.headers.get("content-type")?.includes("application/json");

	return isJSON ? response.json() : response.text();
};

/**
 * handle catch
 * throws HTTPError
 */
const handleCatch = async (responseOrError: Response | Error): Promise<never> => {
	// sometimes server responses with broken response, in this case `fetch` throws itself errors
	// for more information check https://fetch.spec.whatwg.org/#headers-class
	if (isError(responseOrError)) {
		throw new HTTPError({ statusText: "Unknown error", message: responseOrError.message });
	}

	const { url, status: statusCode, statusText } = responseOrError;

	const payload = await parseResponse(responseOrError);

	if (hasProperty(payload, "_type") && payload._type === "TopologyOverflowError") {
		throw new TypologyOverflowError({ statusText, statusCode, url });
	}

	if (responseOrError.status === 401) {
		throw new UnauthorizedError({ message: "Unauthorized", statusText, url });
	}

	if (responseOrError.status === 403) {
		throw new ForbiddenError({ message: "Forbidden", statusText, url });
	}

	if (responseOrError.status === 503) {
		isMaintenanceBoundaryActive.setValue(true);

		throw new HTTPError({ message: "Service Unavailable", statusText, statusCode, url });
	}

	// If the errorResponse looks like ErrorResponse backend type
	// then concat all the individual error messages
	if (isErrorsResponse(payload)) {
		const message = payload.errors.map(({ message }) => message).join("\n");

		if (responseOrError.status === 404) {
			throw new NotFoundError({ apiErrors: payload.errors, message, statusText, url });
		}

		throw new HTTPError({ apiErrors: payload.errors, message, statusText, statusCode, url });
	}

	if (isStackElementNotFoundResponse(payload)) {
		if (responseOrError.status === 404) {
			throw new NotFoundError({ message: "Not Found", statusText, url, data: payload });
		}

		throw new HTTPError({ message: "Not Found", statusText, url, data: payload });
	}

	if (responseOrError.status === 404) {
		throw new NotFoundError({ message: "Not Found", statusText, url });
	}

	// if the response is any type of Dto
	if (isDto(payload)) {
		throw new HTTPError({
			message: "Server error",
			dto: payload,
			statusText,
			statusCode,
			url,
		});
	}

	// The second case handles a list of errors as either strings or ApiErrors
	if (isArray(payload) && payload.length !== 0) {
		const message = payload
			.map((error: ApiError | string) => {
				if (isString(error)) {
					return error;
				}

				if (isApiError(error) && error.message) {
					return error.message;
				}

				if (isApiError(error) && error.errorCode) {
					return `Error code ${error.errorCode}`;
				}

				return null;
			})
			.filter(isPresent)
			.join("\n");

		if (message) {
			throw new HTTPError({ statusText, message, statusCode, url });
		}
	}

	if (typeof payload === "string") {
		throw new HTTPError({ message: payload, statusText });
	}

	throw new HTTPError({
		message: "Unknown server error",
		data: payload,
		statusText,
		statusCode,
		url,
	});
};

export const toRequestObservable =
	<E, P, T, M = unknown>(request: (params: P) => Promise<RequestStatus<E, T, M>>) =>
	(params: P) =>
		from(request(params)).pipe(startWith(loadingStatus<M>()));

export interface ParamsMeta<P> {
	params: P;
}
export function toResponsePromiseWithMeta<E, T, P>(request: (params: P) => Promise<T>) {
	return (params: P): Promise<ResponseStatus<E, T, ParamsMeta<P>>> => {
		const meta: ParamsMeta<P> = { params };
		return request(params)
			.then((response) => successStatus<T, ParamsMeta<P>>(response, meta))
			.catch((error) => errorStatus(error, meta));
	};
}

export function toResponseObservableWithMeta<E, T, P>(request: (params: P) => Promise<T>) {
	return (params: P): Observable<RequestStatus<E, T, ParamsMeta<P>>> => {
		const meta: ParamsMeta<P> = { params };
		return from(
			request(params)
				.then((response) => successStatus<T, ParamsMeta<P>>(response, meta))
				.catch((error) => errorStatus(error, meta)),
		).pipe(startWith(loadingStatus(meta)));
	};
}

const DEFAULT_URL_OPTIONS: StringifyOptions = {
	arrayFormat: "comma",
};

export function stringifyUrl(object: UrlObject, options?: StringifyOptions): string {
	return qs.stringifyUrl(object, { ...DEFAULT_URL_OPTIONS, ...options });
}

export function parseUrl(url: string, options?: ParseOptions): ParsedUrl {
	return qs.parseUrl(url, { ...DEFAULT_URL_OPTIONS, ...options });
}
